import { useState, useEffect, useRef, useMemo } from "react"; 
import { useQuery } from "@tanstack/react-query";
import { getAuth, onAuthStateChanged } from "firebase/auth"; 

import "./DashboardForm.css";
import Navbar from "../components/Navbar/Navbar";
import { apiCall } from "../api/apiCall";
import { User } from "../models/user";
import { Category } from "../models/category";
import { ExpenseReport } from "../models/expense_report";
import TopHeader from '../../src/components/Navbar/TopHeader';
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import StatCard from "../components/StatCard";
import * as echarts from "echarts";

function DashboardForm() {
  const { t } = useTranslation();
  const translationUser = t.users;
  const translationCategory = t.categoryTypes;
  const translationReport = t.expenseReports;

  const categoryChartRef = useRef<HTMLDivElement>(null);
  const rolesChartRef    = useRef<HTMLDivElement>(null);
  const statusChartRef   = useRef<HTMLDivElement>(null);
  const timeChartRef     = useRef<HTMLDivElement>(null);

  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth(); 
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const { data: users, isLoading: isLoadingUsers, isError: isErrorUsers } = useQuery<User[], Error>({
    queryKey: ["fetchAllUsers"],
    queryFn:  async () => apiCall<User[]>("/users", { method: "GET" }),
    enabled: isAuthReady,
  });

  const { data: currentUser, isLoading: isLoadingMe } = useQuery<User, Error>({
    queryKey: ["fetchCurrentUser"],
    queryFn:  async () => apiCall<User>("/users/me", { method: "GET" }),
    enabled: isAuthReady,
  });

  const userToGlimpse   = currentUser as any;
  const userSubscription =
    userToGlimpse?.Company?.Subscription ||
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;
  
  const currentPlan = !currentUser
    ? "none"
    : userSubscription?.status === "active"
      ? userSubscription.plan_code
      : "free";

  const { data: categories, isLoading: isLoadingCategories, isError: isErrorCategories } = useQuery<Category[], Error>({
    queryKey: ["fetchAllCategories"],
    queryFn:  async () => apiCall<Category[]>("/categories", { method: "GET" }),
    enabled: isAuthReady,
  });

  const { data: reports, isLoading: isLoadingReports, isError: isErrorReports } = useQuery<ExpenseReport[], Error>({
    queryKey: ["fetchAllExpenseReports"],
    queryFn:  async () => apiCall<ExpenseReport[]>("/expense-reports", { method: "GET" }),
    enabled: isAuthReady,
  });


  const isLoading = isLoadingUsers || isLoadingCategories || isLoadingReports || isLoadingMe;
  const isError   = isErrorUsers   || isErrorCategories   || isErrorReports;

  const stats = useMemo(() => ({
    members:    users?.length      || 0,
    categories: categories?.length || 0,
    reports:    reports?.length    || 0,
  }), [users, categories, reports]);

  const categoryData = useMemo(() => {
    if (!reports) return [];
    const counts: Record<string, number> = {};
    reports.forEach((report) => {
      const rawType = report.category?.item_type || "";
      const translatedName = rawType 
        ? (translationCategory[rawType.toLowerCase() as keyof typeof translationCategory] || rawType)
        : t.dashboard.unknownStatus;
        
      counts[translatedName] = (counts[translatedName] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [reports, translationCategory, t.dashboard.unknownStatus]);

  const rolesData = useMemo(() => {
    if (!users || users.length === 0) return [];
    const counts: Record<string, number> = {};
    
    users.forEach((user) => {
      let rawRole = "";
      if (typeof user.role === "string") {
        rawRole = user.role;
      } else if (user.role && typeof user.role === "object" && "name" in user.role) {
        rawRole = (user.role as any).name;
      }

      const translatedRole = rawRole 
        ? (translationUser[rawRole.toLowerCase() as keyof typeof translationUser] || rawRole) 
        : t.dashboard.unknownRole;

      const formattedRole = translatedRole.charAt(0).toUpperCase() + translatedRole.slice(1);
      
      counts[formattedRole] = (counts[formattedRole] || 0) + 1;
    });
    
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [users, translationUser, t.dashboard.unknownRole]);

  const statusData = useMemo(() => {
    if (!reports) return { keys: [], values: [] };
    const counts: Record<string, number> = {};

    const getTranslatedStatus = (raw: string) => {
      const s = raw.toLowerCase();
      if (s === 'draft') return translationReport.statusDraft;
      if (s === 'pending') return translationReport.statusPending;
      if (s === 'approved') return translationReport.statusApproved;
      if (s === 'reimbursed') return translationReport.statusReimbursed;
      if (s === 'rejected') return translationReport.statusRejected;
      return raw;
    };

    reports.forEach((report) => {
      const rawStatus = report.status || "";
      const translatedStatus = rawStatus ? getTranslatedStatus(rawStatus) : t.dashboard.unknownStatus;
      counts[translatedStatus] = (counts[translatedStatus] || 0) + 1;
    });
    
    return { keys: Object.keys(counts), values: Object.values(counts) };
  }, [reports, translationReport, t.dashboard.unknownStatus]);

  const timeData = useMemo(() => {
    if (!reports) return { months: [], amounts: [] };
    const monthlyAmounts: Record<string, number> = {};
    reports.forEach((report) => {
      const dateStr = report.expense_date || report.submitted_at;
      if (!dateStr) return;
      const date     = new Date(dateStr);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      monthlyAmounts[monthKey] = (monthlyAmounts[monthKey] || 0) + (Number(report.amount) || 0);
    });
    const sortedMonths = Object.keys(monthlyAmounts).sort();
    return { months: sortedMonths, amounts: sortedMonths.map((m) => monthlyAmounts[m]) };
  }, [reports]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isLoading || isError) return;

    const charts: echarts.ECharts[] = [];
    const theme = isDarkMode ? "dark" : undefined; 

    if (categoryChartRef.current && categoryData.length > 0) {
      const chart = echarts.init(categoryChartRef.current, theme);
      chart.setOption({
        backgroundColor: "transparent", 
        title:   { text: t.dashboard.chartByType, left: "center", textStyle: { fontSize: 15 } },
        tooltip: { trigger: "item", formatter: "{b} : {c} ({d}%)" },
        legend:  { type: "scroll", bottom: "0" },
        series:  [{ type: "pie", radius: ["40%", "55%"], center: ["50%", "45%"], data: categoryData, label: { show: false } }],
      });
      charts.push(chart);
    }

    if (rolesChartRef.current && rolesData.length > 0) {
      const chart = echarts.init(rolesChartRef.current, theme);
      chart.setOption({
        backgroundColor: "transparent",
        title:   { text: t.dashboard.chartByUsers, left: "center", textStyle: { fontSize: 15 } },
        tooltip: { trigger: "item", formatter: "{b} : {c} ({d}%)" },
        legend:  { bottom: "0" },
        series:  [{ type: "pie", radius: "55%", center: ["50%", "45%"], data: rolesData, label: { show: false }, itemStyle: { borderRadius: 5 } }],
      });
      charts.push(chart);
    }

    if (statusChartRef.current && statusData.keys.length > 0) {
      const chart = echarts.init(statusChartRef.current, theme);
      chart.setOption({
        backgroundColor: "transparent",
        title:   { text: t.dashboard.chartByStatus, left: "center", textStyle: { fontSize: 15 } },
        tooltip: { trigger: "axis" },
        grid:    { left: "15%", right: "10%", bottom: "15%" },
        xAxis:   { type: "category", data: statusData.keys, axisLabel: { interval: 0, fontSize: 10 } },
        yAxis:   { type: "value" },
        series:  [{ data: statusData.values, type: "bar", itemStyle: { color: "#3498db", borderRadius: [4, 4, 0, 0] } }],
      });
      charts.push(chart);
    }

    if (timeChartRef.current && timeData.months.length > 0) {
      const chart = echarts.init(timeChartRef.current, theme);
      chart.setOption({
        backgroundColor: "transparent",
        title:   { text: t.dashboard.chartOverTime, left: "left", textStyle: { fontSize: 16 } },
        tooltip: { trigger: "axis", formatter: "{b} : {c} €" },
        grid:    { left: "5%", right: "5%", bottom: "10%", containLabel: true },
        xAxis:   { type: "category", boundaryGap: false, data: timeData.months },
        yAxis:   { type: "value" },
        series:  [{
          data: timeData.amounts, type: "line", smooth: true,
          areaStyle:  { opacity: 0.3, color: "#2ecc71" },
          lineStyle:  { color: "#2ecc71", width: 3 },
          itemStyle:  { color: "#2ecc71" },
        }],
      });
      charts.push(chart);
    }

    const handleResize = () => charts.forEach((c) => c.resize());
    window.addEventListener("resize", handleResize);

    return () => {
      charts.forEach((c) => c.dispose());
      window.removeEventListener("resize", handleResize);
    };
  }, [isLoading, isError, categoryData, rolesData, statusData, timeData, t, isDarkMode]);

  if (isLoading) {
    return (
      <div style={{ paddingTop: "70px" }}>
        <Navbar />
        <div className="dashboard-wrapper dashboard-state-message">
          <TopHeader />
          <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName="le Tableau de bord complet" minHeight={500}>
            <div className="spinner" />
            <p>{t.dashboard.loading}</p>
          </FeatureGate>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ paddingTop: "70px" }}>
        <Navbar />
        <div className="dashboard-wrapper dashboard-state-message error-message">
          <TopHeader />
          <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName="le Tableau de bord complet" minHeight={500}>
            <p>{t.dashboard.errorLoading}</p>
          </FeatureGate>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="dashboard-wrapper">
        <TopHeader />

        <main className="dashboard-content">
          <h1 className="dashboard-title">{t.nav.dashboard}</h1>
          <p className="dashboard-subtitle">{t.dashboard.subtitle}</p>

          <FeatureGate userPlan={currentPlan} requiredPlan="free" featureName="le Tableau de bord complet" minHeight={500}>

            <section className="stats-grid">
              <StatCard title={t.dashboard.totalMembers}     value={stats.members}    variant="primary" />
              <StatCard title={t.dashboard.activeCategories} value={stats.categories} variant="success" />
              <StatCard title={t.dashboard.expenseReports}   value={stats.reports}    variant="warning" />
            </section>

            <section className="chart-section time-chart-section">
              <div ref={timeChartRef} className="echarts-container" style={{ height: "350px" }} />
            </section>

            <section className="charts-grid-bottom">
              <div className="chart-card">
                <div ref={categoryChartRef} className="echarts-container" style={{ height: "300px" }} />
              </div>
              <div className="chart-card">
                <div ref={rolesChartRef} className="echarts-container" style={{ height: "300px" }} />
              </div>
              <div className="chart-card">
                <div ref={statusChartRef} className="echarts-container" style={{ height: "300px" }} />
              </div>
            </section>

          </FeatureGate>
        </main>
      </div>
    </div>
  );
}

export default DashboardForm;